// team-page.js — Team page inner renderer.
//
// Owns renderTeamPageInner: paints the connected/not-connected
// banners, the role badge, the per-person member list (with
// nested machines), the invite-code block, and dispatches to the
// settings + Team-v2 sub-renderers.
//
// The thin renderTeamPage wrapper that consults the 5s
// teamState.lastFetch cache and dispatches a re-fetch when stale
// lives in pages/team.js (page-render lifecycle).
//
// Sub-renderer state as of Phase J6h-d:
//   - fetchTeamSettings: src/team-settings.js (J6h-b)
//   - renderTeamV2 + renderTeamSwitcher: still in legacy
//     (touch supabase auth state — hoist with Phase J7)
//   - renderTeamGovernanceEditor: deleted as dead code (J6h-d)
//     (superseded by the visual rule builder in May 2026)
//
// fetchTeamSettings is now a direct import. renderTeamV2 is
// looked up via the legacy bridge to dodge the auth coupling.
import { teamState } from "./page-state.js";
import { $ } from "./dom.js";
import { escapeHTML, ago, parseUTC } from "./util.js";

// memberIsActive: same heuristic as widgets.js connectionPill — trust
// EITHER the coordinator's coarse is_online boolean OR a fresh
// last_seen timestamp from the local view. Coordinator's is_online
// can lag behind real pushes (Vincent's stale-pill incident); fresh
// last_seen catches that case.
const MEMBER_ACTIVE_WINDOW_MS = 60_000;
function memberIsActive(m) {
  if (!m) return false;
  if (m.is_online) return true;
  if (!m.last_seen) return false;
  const ms = parseUTC(m.last_seen);
  return Number.isFinite(ms) && Date.now() - ms < MEMBER_ACTIVE_WINDOW_MS;
}
import { fetchTeamSettings } from "./team-settings.js";
// As of PR #130 Roles moved to its own top-level page (pages/roles.js).
// As of PR #131 the Workspaces panel moved to the Settings page
// (pages/settings.js handles renderBusPanel + wireBuCreate). The Team
// page no longer touches either; this module renders only the
// per-tenant member roster + Team-v2 invite/seat panel.

// legacyHelper resolves a fn off the AC_LEGACY bridge at call
// time. Used for renderTeamV2 only (still bootstrap-owned because
// it touches supabase auth state — hoists with the deferred auth
// hoist). The bridge populates after bootstrap.js's IIFE finishes,
// so reading at call time (not at module init) is the safe pattern.
function legacyHelper(name) {
  return (window.AC_LEGACY && window.AC_LEGACY[name]) || null;
}

export function renderTeamPageInner() {
  const notConn = $("team-not-connected");
  const conn = $("team-connected");
  if (!notConn || !conn) return;

  // A local daemon that isn't on a team answers /v1/teams/info with a solo
  // stub. It has no team to administer, so it gets the solo panel too.
  if (!teamState.info || teamState.info.solo) {
    notConn.style.display = "";
    conn.style.display = "none";
    return;
  }

  notConn.style.display = "none";
  conn.style.display = "";

  const info = teamState.info;
  // Owner role retired in migration 035 — treat your_team_role === "owner"
  // as admin for back-compat with JWTs minted pre-migration.
  const isAdmin = info.your_role === "admin"
    || info.your_team_role === "admin"
    || info.your_team_role === "owner";

  $("team-name-text").textContent = info.team_name || "Team";
  $("team-name-edit-btn").style.display = isAdmin ? "" : "none";
  $("team-name-display").style.display = "";
  $("team-name-edit-mode").style.display = "none";
  $("team-member-count").textContent =
    (info.member_count || 0) +
    " member" +
    (info.member_count === 1 ? "" : "s");
  $("team-subtitle").textContent =
    "Connected to " + (info.team_name || "your team");
  const badge = $("team-role-badge");
  // Two pills: admin (accent) > member (subtle). Matches the in-list
  // role pills below and the API-keys scope picker.
  if (isAdmin) {
    badge.textContent = "Admin";
    badge.style.background = "var(--accent)";
    badge.style.color = "white";
  } else {
    badge.textContent = "Member";
    badge.style.background = "var(--bg-elevated)";
    badge.style.color = "var(--text-secondary)";
  }

  // Hide the legacy no-owner banner (the Owner role no longer exists).
  const noOwnerBanner = $("team-no-owner-banner");
  if (noOwnerBanner) noOwnerBanner.style.display = "none";

  // Workspaces panel moved to the Settings page in PR #131
  // (pages/settings.js owns renderBusPanel + wireBuCreate). The Team
  // page no longer renders or wires it.

  // Render members
  const list = $("team-members-list");
  const people = teamState.people || [];

  // Prefer the identity-grouped view when available — shows each person with
  // their machines nested. Falls back to the flat laptop view for teams
  // predating the v2 schema (no user_id on tokens).
  if (people.length > 0) {
    list.innerHTML = people
      .map((p) => {
        const dotColor = p.is_online
          ? "var(--success)"
          : "var(--text-tertiary)";
        const dotShadow = p.is_online
          ? "box-shadow:0 0 8px rgba(52,211,153,0.6);"
          : "";
        // Two role pills: admin (accent), member (default). Legacy
        // 'owner' values from rows pre-migration 035 render as admin.
        let rolePill;
        if (p.role === "admin" || p.role === "owner") {
          rolePill =
            '<span style="font-size: 10px; padding: 1px 8px; border-radius: 999px; background: var(--accent); color: white; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; margin-left: 8px;">admin</span>';
        } else {
          rolePill =
            '<span style="font-size: 10px; padding: 1px 8px; border-radius: 999px; background: var(--bg-elevated); color: var(--text-secondary); font-weight: 500; text-transform: uppercase; letter-spacing: 0.5px; margin-left: 8px;">member</span>';
        }
        // Seniority + title chips. Both are optional metadata
        // (added in migration 024 — see members.seniority). When
        // unset, render nothing rather than an empty pill.
        const seniorityPill = p.seniority
          ? '<span class="chip chip-info" style="margin-left: 6px; text-transform: capitalize;">' +
            escapeHTML(p.seniority) +
            "</span>"
          : "";
        const titleLabel = p.title
          ? '<span style="color: var(--text-secondary); font-size: 12.5px; margin-left: 8px;">&middot; ' +
            escapeHTML(p.title) +
            "</span>"
          : "";
        const lastSeenLabel = p.last_seen ? ago(p.last_seen) : "never connected";
        const machineCount = p.machines.length;
        const machineLabel =
          machineCount === 0
            ? '<span style="color: var(--warning);">no machines yet</span>'
            : machineCount + " machine" + (machineCount === 1 ? "" : "s");

        const machinesHtml =
          p.machines.length > 0
            ? p.machines
                .map((m) => {
                  const active = memberIsActive(m);
                  const mDot = active
                    ? '<span style="display:inline-block;width:6px;height:6px;border-radius:50%;background:var(--success);margin-right:6px;"></span>'
                    : '<span style="display:inline-block;width:6px;height:6px;border-radius:50%;background:var(--text-tertiary);margin-right:6px;"></span>';
                  const mLast = m.last_seen ? ago(m.last_seen) : "never";
                  const ip = m.last_used_ip
                    ? " &middot; " + escapeHTML(m.last_used_ip)
                    : "";
                  const revokeBtn = isAdmin
                    ? `<button class="btn btn-sm" data-team-remove="${escapeHTML(m.laptop_id)}" data-revoke-computer="${escapeHTML(m.label)}" style="font-size: 10px; padding: 2px 8px; color: var(--danger); border-color: var(--danger);">Revoke</button>`
                    : "";
                  return `<div style="display: flex; align-items: center; justify-content: space-between; padding: 6px var(--s4) 6px 40px; border-top: 1px solid var(--border); gap: var(--s2);">
                <div style="min-width: 0; font-size: 12px; color: var(--text-secondary);">
                  ${mDot}<strong style="color: var(--text);">${escapeHTML(m.label)}</strong>
                  <span style="color: var(--text-tertiary); font-family: var(--font-mono); font-size: 11px; margin-left: 6px;">${escapeHTML(m.laptop_id)}</span>
                  <div style="color: var(--text-tertiary); margin-top: 2px; margin-left: 14px; font-size: 11px;">
                    ${active ? "online now" : "last seen " + mLast}${ip}
                  </div>
                </div>
                ${revokeBtn}
              </div>`;
                })
                .join("")
            : `<div style="padding: 6px var(--s4) 10px 40px; border-top: 1px solid var(--border); font-size: 12px; color: var(--text-tertiary); font-style: italic;">
              Hasn't run the install command yet.
             </div>`;

        // Action buttons (visible to admins only): Profile / Promote-Demote
        // / Remove. Owner role retired in migration 035 — Transfer ownership
        // affordance removed.
        let adminActions = "";
        if (isAdmin) {
          const isTargetAdmin = p.role === "admin" || p.role === "owner";
          adminActions = `
          <div style="display: flex; gap: var(--s2); align-items: center;">
            <button class="btn btn-sm" data-team-edit-profile="${escapeHTML(p.user_id)}" data-email="${escapeHTML(p.email)}" data-title="${escapeHTML(p.title || "")}" data-seniority="${escapeHTML(p.seniority || "")}" style="font-size: 11px; padding: 2px 10px;" title="Set title and seniority">Profile</button>
            <button class="btn btn-sm" data-team-change-role="${escapeHTML(p.user_id)}" data-current-role="${isTargetAdmin ? "admin" : "member"}" style="font-size: 11px; padding: 2px 10px;">
              ${isTargetAdmin ? "Demote" : "Promote"}
            </button>
            <button class="btn btn-sm" data-team-remove-user="${escapeHTML(p.user_id)}" data-email="${escapeHTML(p.email)}" style="font-size: 11px; padding: 2px 10px; color: var(--danger); border-color: var(--danger);">Remove</button>
          </div>`;
        }

        return `<div style="border-bottom: 1px solid var(--border);" data-member-name="${escapeHTML(p.email)}">
          <div style="display: flex; align-items: center; justify-content: space-between; padding: 12px var(--s4); gap: var(--s3);">
            <div style="min-width: 0; flex: 1; display: flex; align-items: center;">
              <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${dotColor};${dotShadow}margin-right:10px;flex-shrink:0;"></span>
              <div style="min-width: 0;">
                <div style="display: flex; align-items: center; flex-wrap: wrap;">
                  <span style="color: var(--text); font-weight: 500; font-size: 14px;">${escapeHTML(p.email)}</span>${rolePill}${seniorityPill}${titleLabel}
                </div>
                <div style="color: var(--text-tertiary); font-size: 12px; margin-top: 2px;">
                  ${machineLabel} &middot; joined ${ago(p.joined_at)} &middot; ${p.is_online ? "online now" : "last active " + lastSeenLabel}
                </div>
              </div>
            </div>
            ${adminActions}
          </div>
          ${machinesHtml}
        </div>`;
      })
      .join("");
    // Computers whose login belongs to no one (made before accounts were
    // linked). They still hold a team role, so list them where they can be
    // revoked; otherwise they're invisible here.
    const owned = new Set(people.flatMap((p) => p.machines.map((m) => m.laptop_id)));
    const unowned = (teamState.members || []).filter(
      (m) => m.laptop_id && !m.laptop_id.startsWith("user:") && !owned.has(m.laptop_id),
    );
    if (unowned.length > 0) {
      list.innerHTML +=
        `<div style="padding: 10px var(--s4) 4px; font-size: 11px; font-weight: 600; letter-spacing: 0.5px; text-transform: uppercase; color: var(--text-tertiary); border-bottom: 1px solid var(--border);">Computers not linked to anyone</div>` +
        unowned
          .map((m) => {
            const name = m.label || m.display_name || m.laptop_id;
            const seen = m.is_online ? "online now" : m.last_seen ? "last seen " + ago(m.last_seen) : "last seen never";
            const role = m.role === "admin" || m.role === "owner" ? " &middot; admin access" : "";
            const revokeBtn = isAdmin
              ? `<button class="btn btn-sm" data-team-remove="${escapeHTML(m.laptop_id)}" data-revoke-computer="${escapeHTML(name)}" style="font-size: 10px; padding: 2px 8px; color: var(--danger); border-color: var(--danger);">Revoke</button>`
              : "";
            return `<div style="display: flex; align-items: center; justify-content: space-between; padding: 8px var(--s4); border-bottom: 1px solid var(--border); gap: var(--s2);">
              <div style="min-width: 0; font-size: 12px; color: var(--text-secondary);">
                <strong style="color: var(--text);">${escapeHTML(name)}</strong>
                ${name !== m.laptop_id ? `<span style="color: var(--text-tertiary); font-family: var(--font-mono); font-size: 11px; margin-left: 6px;">${escapeHTML(m.laptop_id)}</span>` : ""}
                <div style="color: var(--text-tertiary); margin-top: 2px; font-size: 11px;">${seen}${role}</div>
              </div>
              ${revokeBtn}
            </div>`;
          })
          .join("");
    }
    const computers = owned.size + unowned.length;
    $("team-member-count").textContent =
      people.length + (people.length === 1 ? " person" : " people") +
      " · " + computers + (computers === 1 ? " computer" : " computers");
    // Done — skip the legacy laptop view
  } else if (teamState.members.length === 0) {
    list.innerHTML =
      '<div style="padding: var(--s6) var(--s4); text-align: center;">' +
      '<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="margin-bottom: var(--s3);"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>' +
      '<div style="color: var(--text-secondary); font-size: 13px;">No members yet. Invite teammates above.</div>' +
      "</div>";
  } else {
    list.innerHTML = teamState.members
      .map((m) => {
        const onlineDot = m.is_online
          ? '<span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: var(--success); margin-right: 8px;" title="Online"></span>'
          : '<span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: var(--text-tertiary); margin-right: 8px;" title="Offline"></span>';
        const rolePill =
          m.role === "admin"
            ? '<span style="font-size: 10px; padding: 1px 8px; border-radius: 999px; background: var(--accent); color: white; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; margin-left: 8px;">admin</span>'
            : '<span style="font-size: 10px; padding: 1px 8px; border-radius: 999px; background: var(--bg-elevated); color: var(--text-secondary); font-weight: 500; text-transform: uppercase; letter-spacing: 0.5px; margin-left: 8px;">member</span>';
        const youTag = m.is_you
          ? '<span style="color: var(--text-tertiary); font-size: 12px; margin-left: 6px;">(you)</span>'
          : "";
        const lastSeen = m.last_seen ? ago(m.last_seen) : "never";
        const canRename = m.is_you || isAdmin;
        const renameBtn = canRename
          ? `<button data-team-rename="${escapeHTML(m.laptop_id)}" data-current-name="${escapeHTML(m.display_name)}" style="background: none; border: none; cursor: pointer; color: var(--accent); padding: 0 6px; font-size: 14px;" title="Rename">&#9998;</button>`
          : "";
        const editNameRow = canRename
          ? `<div data-member-name-edit style="display: none; align-items: center; gap: var(--s2);">
              <input type="text" maxlength="100" style="width: 160px; padding: 3px 6px; font-size: 13px; background: var(--bg-elevated); border: 1px solid var(--accent); border-radius: var(--r-sm); color: var(--text); outline: none;" />
              <button class="btn btn-sm" data-member-save-name="${escapeHTML(m.laptop_id)}" style="font-size: 11px; padding: 2px 8px; background: var(--accent); color: white; border: none;">Save</button>
              <button class="btn btn-sm" data-member-cancel-name style="font-size: 11px; padding: 2px 8px;">Cancel</button>
            </div>`
          : "";
        const adminActions =
          isAdmin && !m.is_you
            ? `
          <div style="display: flex; gap: var(--s2); align-items: center;">
            <button class="btn btn-sm" data-team-toggle-role="${escapeHTML(m.laptop_id)}" data-current-role="${m.role}" style="font-size: 11px; padding: 2px 10px;">
              ${m.role === "admin" ? "Demote" : "Promote"}
            </button>
            <button class="btn btn-sm" data-team-remove="${escapeHTML(m.laptop_id)}" style="font-size: 11px; padding: 2px 10px; color: var(--danger); border-color: var(--danger);">Remove</button>
          </div>`
            : "";
        return `<div data-member-row style="display: flex; align-items: center; justify-content: space-between; padding: 12px var(--s4); border-bottom: 1px solid var(--border);">
          <div style="display: flex; align-items: center; min-width: 0;">
            ${onlineDot}
            <div style="min-width: 0;">
              <div data-member-name-display style="display: flex; align-items: center; flex-wrap: wrap;">
                <span style="color: var(--text); font-weight: 500; font-size: 14px;">${escapeHTML(m.display_name)}</span>${renameBtn}
                ${rolePill}${youTag}
              </div>
              ${editNameRow}
              <div style="color: var(--text-tertiary); font-size: 12px; font-family: var(--font-mono); margin-top: 2px;">
                ${escapeHTML(m.laptop_id)} &middot; last seen ${lastSeen}
              </div>
            </div>
          </div>
          ${adminActions}
        </div>`;
      })
      .join("");
  }

  // Invite section
  const hasCode = $("team-invite-has-code");
  const noCode = $("team-invite-no-code");
  const loadingCode = $("team-invite-loading");
  loadingCode.style.display = "none";
  if (teamState.inviteCode) {
    hasCode.style.display = "";
    noCode.style.display = "none";
    $("team-invite-display").textContent =
      "agc team join " + teamState.inviteCode;
    $("team-invite-actions").style.display = isAdmin ? "flex" : "none";
  } else {
    hasCode.style.display = "none";
    if (isAdmin) {
      noCode.style.display = "";
    } else {
      noCode.style.display = "";
      noCode.innerHTML =
        '<div style="text-align: center; padding: var(--s4) 0;"><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="margin-bottom: var(--s3);"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg><p style="color: var(--text-secondary); font-size: 13px;">Ask an admin to generate an invite code.</p></div>';
    }
  }

  const dangerZone = $("team-danger-zone");
  if (dangerZone) dangerZone.style.display = isAdmin ? "" : "none";

  // Load & render team settings (coordination policy).
  fetchTeamSettings();

  // Team v2: seat counter, invitations, pending list — still
  // legacy-owned (hoists with auth/Supabase in Phase J7).
  const renderTeamV2 = legacyHelper("renderTeamV2");
  if (renderTeamV2) renderTeamV2(isAdmin);
}
